import React from "react";

import { IClassParam } from "../../../managerStore";
import { IClass, ISubClass, ITeacher } from "../../../state";
import { thisSchoolYear } from "../../../libs/Util";

import styles from "./SubClassForm.module.scss";

export interface SubClassFormProps {
  title: string;
  calling: boolean;
  classes: IClass[];
  teachers: ITeacher[];
  teacher_ids: number[];
  sub_class?: ISubClass;
  upsertSubClass: (cls: IClassParam) => void;
}

const SubClassForm = (props: SubClassFormProps) => {
  const [state, setState] = React.useState<{
    name: string;
    teacher_ids: number[];
  }>({
    name: "",
    teacher_ids: [],
  });

  React.useEffect(() => {
    setState({
      name: props.sub_class?.full_name ?? "",
      teacher_ids: props.teacher_ids,
    });
  }, [props.sub_class, props.teacher_ids]);

  const id = React.useMemo(() => {
    return props.sub_class ? props.sub_class.id : 0;
  }, [props.sub_class]);

  const year = React.useMemo(() => {
    return props.sub_class ? props.sub_class.year : thisSchoolYear();
  }, [props.sub_class]);

  const isDisplayFullName = React.useMemo(() => {
    return props.sub_class ? !!props.sub_class.is_display_full_name : false;
  }, [props.sub_class]);

  const [searchName, setSearchName] = React.useState("");
  const teachers = React.useMemo(() => {
    let teachers = props.teachers;
    if (searchName) {
      const lowercasedSearch = searchName.toLowerCase();
      teachers = teachers.filter(t => {
        const fullName = `${t.last_name} ${t.first_name}`;
        return fullName.toLowerCase().includes(lowercasedSearch);
      });
    }
    return teachers;
  }, [props.teachers, searchName]);

  const bulkChecked = React.useMemo(() => {
    if (teachers.length === 0) return false;
    return teachers.every(s => state.teacher_ids.includes(s.id));
  }, [teachers, state.teacher_ids]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <h3 className="mb-0 me-3">{props.title}</h3>
        <span>
          グループ名を入力し担当の先生を選択後、作成ポタンを押してください。所属の生徒の登録はグループ作成後可能となります。
        </span>
      </div>
      <div className={styles.inputArea}>
        <div className="mb-3">●グループ名を入力してください</div>
        <input
          value={state.name}
          className="form-control"
          placeholder="例) 文化祭実行委員"
          onChange={e => setState({ ...state, name: e.target.value })}
        />
      </div>
      <div className={styles.searchArea}>
        <div className={styles.label}>
          ●グループを担当する先生を選択してください
        </div>
        <div className="d-flex align-items-center">
          <label className="me-3 font-weight-bold">名前検索</label>
          <input
            value={searchName}
            className="form-control mb-0"
            placeholder="氏名を入力"
            onChange={e => setSearchName(e.target.value)}
          />
        </div>
      </div>
      <div className={styles.table}>
        <table className="table table-striped table-hover mb-0">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulkChecked}
                  disabled={teachers.length === 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const tIds = e.currentTarget.checked
                      ? teachers.map(t => t.id)
                      : state.teacher_ids.filter(
                          ti => !teachers.map(t => t.id).includes(ti),
                        );
                    setState({ ...state, teacher_ids: tIds });
                  }}
                />
              </th>
              <th>ID</th>
              <th>名前</th>
              <th>Email</th>
              <th>担当クラス</th>
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {teachers.map(t => {
              return (
                <tr key={t.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={state.teacher_ids.indexOf(t.id) !== -1}
                      value={t.id}
                      onChange={() => {
                        if (state.teacher_ids.indexOf(t.id) === -1) {
                          setState({
                            ...state,
                            teacher_ids: [...state.teacher_ids, t.id].sort(
                              (a, b) => a - b,
                            ),
                          });
                        } else {
                          setState({
                            ...state,
                            teacher_ids: state.teacher_ids.filter(
                              id => id !== t.id,
                            ),
                          });
                        }
                      }}
                    />
                  </td>
                  <td>{t.id}</td>
                  <td>{`${t.last_name} ${t.first_name}`}</td>
                  <td>{t.email}</td>
                  <td>
                    {props.classes
                      .filter(c => t.class_ids.indexOf(c.id) !== -1)
                      .map(c => (
                        <div key={`${c.id}`}>{c.full_name}</div>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        disabled={state.name.length <= 0 || props.calling}
        className="btn btn-primary"
        onClick={() => {
          props.upsertSubClass({
            id,
            name: state.name,
            year,
            grade: 0,
            is_display_full_name: isDisplayFullName,
            teacher_ids: state.teacher_ids,
            is_sub: true,
          });
        }}
      >
        {id ? "更新" : "登録"}
      </button>
    </div>
  );
};

export default SubClassForm;
